import clsx from 'clsx';
import React from 'react';
import { DefaultToast } from 'react-toast-notifications';
import Icon from '../../atoms/Icon/Icon';
import type { ToastProps } from './types';
import useToast from './useToast';

/**
 * Toast — v2 molecule.
 *
 * Presentational wrapper over `DefaultToast` from `react-toast-notifications`.
 * Registered once in the `<ToastProvider>` (`src/App.js`) and fired from
 * anywhere with `useToasts().addToast(message, { appearance })`.
 *
 * Accessibility:
 *  - No `aria-live` here on purpose: the live region is `ToastContainer`, which
 *    is already mounted when the toast is inserted into it. A second, nested
 *    region would announce the message twice.
 *  - Own dismiss button, visible in every appearance and with `aria-label` (the
 *    library's own button exposes no accessible name and is hidden via CSS).
 *  - The timeout (>= 5s per WCAG) is set with `autoDismissTimeout` in the `ToastProvider`.
 */
function Toast({ appearance, children, onDismiss, ...props }: Readonly<ToastProps>): JSX.Element {
  const { dismissAriaLabel, iconName } = useToast({ appearance });

  return (
    <DefaultToast appearance={appearance} onDismiss={onDismiss} {...props}>
      <div className={clsx('toast', appearance && `toast--${appearance}`)}>
        {iconName && <Icon color="inherit" name={iconName} size="lg" />}
        <span className="toast__content">{children}</span>
        <button aria-label={dismissAriaLabel} className="toast__dismiss" onClick={onDismiss} type="button">
          <Icon color="inherit" name="close" size="sm" />
        </button>
      </div>
    </DefaultToast>
  );
}

export default Toast;
